<template>
  <div>
    <!-- Leading Items -->
    <div
      v-for="item in leadingItems"
      :key="item.id ? item.id : undefined"
      class="mb-4"
      :class="itemClass"
    >
      <slot :item="item" name="item">
        <p>Item {{ item.id }}</p>
      </slot>
    </div>
    <div v-for="i in leadingLoading" class="mb-4">
      <slot name="loading">
        <DefaultSkeletonLoader />
      </slot>
    </div>

    <template v-if="!allLoaded && loadOnlyLeading">
      <!-- Load More (infinite loading) -->
      <slot
        name="loadMore"
        v-bind:loadMore="() => loadLeading()"
        v-bind:remainingCount="remainingCount"
        v-bind:nextPageSize="nextPageSize"
      >
        <TextButton
          :label="`Ältere ${nextPageSize} laden (${remainingCount} verbleibend)`"
          variant="small"
          @click="loadLeading()"
        />
      </slot>
    </template>

    <template v-else-if="!allLoaded && isLastPage">
      <!-- Load Remaining -->
      <slot
        name="loadRemaining"
        v-bind:loadMore="() => loadRemaining()"
        v-bind:remainingCount="remainingCount"
        v-bind:nextPageSize="nextPageSize"
      >
        <TextButton
          variant="small"
          @click="loadRemaining()"
          :label="`Alle übrigen ${remainingCount} laden`"
        />
      </slot>
    </template>

    <template v-else-if="!allLoaded">
      <!-- Load in between -->
      <slot
        name="loadIntermediate"
        v-bind:loadLeading="() => loadLeading()"
        v-bind:loadTrailing="() => loadTrailing()"
        v-bind:remainingCount="remainingCount"
        v-bind:nextPageSize="nextPageSize"
      >
        <TextButton
          @click="loadLeading()"
          variant="small"
          :label="`Vorherige ${nextPageSize} laden`"
        />
        ({{ remainingCount }} noch ausgeblendet)
        <TextButton
          @click="loadTrailing()"
          variant="small"
          :label="`Spätere ${nextPageSize} laden`"
        />
      </slot>
    </template>

    <!-- Trailing Items -->
    <div v-for="i in trailingLoading" class="mt-4">
      <slot name="loading">
        <DefaultSkeletonLoader />
      </slot>
    </div>
    <div
      v-for="item in trailingItems"
      class="mt-4"
      :key="item.id ? item.id : undefined"
    >
      <slot :item="item" name="item">
        <p>Item {{ item.id }}</p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue"
import {
  LoadMoreFunction,
  useOmissionLoaderInternal,
} from "features/omission-loader/use-omission-loader-internal"
import DefaultSkeletonLoader from "ui-primitives/loading-indicators/DefaultSkeletonLoader.vue"
import { Identifiable } from "features/common/shared/polymorphic"
import TextButton from "components/UI/TextButton.vue"
import { removeWhere, updateWhere } from "app/util/ref-list.util"

type UpdateFunction<T extends Identifiable> = (item: T) => T

export default defineComponent({
  name: "OmissionLoader",
  components: {
    TextButton,
    DefaultSkeletonLoader,
  },
  props: {
    initialLeadingCount: {
      type: Number,
      required: true,
    },
    initialTrailingCount: {
      type: Number,
      required: true,
    },
    pageSize: {
      type: Number,
      required: true,
    },
    totalCount: {
      type: Number,
      required: true,
    },
    loadMore: {
      type: Function as PropType<LoadMoreFunction>,
      required: true,
    },
    itemClass: {
      type: String,
      default: "",
    },
  },
  setup(props, context) {
    const leadingItems = ref([])
    const trailingItems = ref([])

    const {
      isLastPage,
      allLoaded,
      loadOnlyLeading,
      loadLeading,
      loadTrailing,
      loadRemaining,
      trailingLoading,
      leadingLoading,
      remainingCount,
      nextPageSize,
    } = useOmissionLoaderInternal(
      props.totalCount,
      props.pageSize,
      props.initialLeadingCount,
      props.initialTrailingCount,
      props.loadMore,
      (items: Identifiable[]) => {
        leadingItems.value.push(...items)
        context.emit("leadingLoaded", items)
        emitTotalLoaded()
      },
      (items: Identifiable[]) => {
        trailingItems.value.unshift(...items)
        context.emit("trailingLoaded", items)
        emitTotalLoaded()
      }
    )

    const emitTotalLoaded = () => {
      context.emit(
        "totalLoaded",
        leadingItems.value.length + trailingItems.value.length
      )
    }

    const addItemToLeading = (item: Identifiable) => {
      leadingItems.value.unshift(item)
      emitTotalLoaded()
    }

    const addItemToTrailing = (item: Identifiable) => {
      trailingItems.value.push(item)
      emitTotalLoaded()
    }

    const updateItem = <T extends Identifiable>(
      id: number,
      updateFunction: UpdateFunction<T>
    ) => {
      const isItemInLeading = leadingItems.value.some((i) => i.id === id)
      if (isItemInLeading) {
        updateWhere(
          leadingItems,
          (i) => i.id === id,
          (i) => updateFunction(i)
        )
      } else {
        updateWhere(
          trailingItems,
          (i) => i.id === id,
          (i) => updateFunction(i)
        )
      }
    }

    const removeItem = (id: number) => {
      removeWhere(leadingItems, (i) => i.id === id)
      removeWhere(trailingItems, (i) => i.id === id)
      emitTotalLoaded()
    }

    return {
      isLastPage,
      allLoaded,
      loadOnlyLeading,
      loadLeading,
      loadTrailing,
      loadRemaining,
      trailingLoading,
      leadingLoading,
      leadingItems,
      trailingItems,
      remainingCount,
      nextPageSize,
      // COMPONENT METHODS
      addItemToLeading,
      addItemToTrailing,
      updateItem,
      removeItem,
    }
  },
})
</script>
